/** API events → grid events, and the localized names of show-as and responses. */
import type { MessageKey, Translate } from '@platform/i18n';
import type { CalendarGridEvent } from '@platform/ui';
import type { CalendarEvent, CalendarInfo } from '../api';
import type { Category } from '../api/calendar-extra';
import { categoryOf } from './categories';

export const SHOW_AS_LABEL: Record<CalendarEvent['showAs'], MessageKey> = {
  free: 'webmail.calendar.showAs.free',
  tentative: 'webmail.calendar.showAs.tentative',
  busy: 'webmail.calendar.showAs.busy',
  oof: 'webmail.calendar.showAs.oof',
  workingElsewhere: 'webmail.calendar.showAs.workingElsewhere',
};

export const RESPONSE_LABEL: Record<CalendarEvent['myResponse'], MessageKey> = {
  unknown: 'webmail.calendar.response.unknown',
  organizer: 'webmail.calendar.response.organizer',
  tentative: 'webmail.calendar.response.tentative',
  accepted: 'webmail.calendar.response.accepted',
  declined: 'webmail.calendar.response.declined',
  none: 'webmail.calendar.response.none',
};

/** Display name: the default calendar is "Calendar" (Outlook), every other one has its own name. */
export const calendarName = (calendar: Pick<CalendarInfo, 'id' | 'name' | 'kind'>, t: Translate): string => {
  if (calendar.id === 'own') return t('webmail.calendar.rail.calendar');
  if (calendar.kind === 'birthday') return t('webmail.calendarUx.virtual.birthdays');
  if (calendar.kind === 'holiday') return t('webmail.calendarUx.virtual.holidaysOf', { country: regionName(calendar.name) });
  return calendar.name;
};

/** Country name in the app language (holiday calendars). */
export function regionName(code: string): string {
  try {
    // The app language (the i18n provider sets <html lang>), not the browser's.
    return new Intl.DisplayNames(document.documentElement.lang || undefined, { type: 'region' }).of(code) ?? code;
  } catch {
    return code;
  }
}

/** Free/busy blocks of calendars without read rights have synthetic ids; they cannot be opened. */
export const isBlock = (event: Pick<CalendarEvent, 'id'>): boolean => event.id.startsWith('fb.');

export interface ShownEvent {
  event: CalendarEvent;
  calendar: CalendarInfo;
  grid: CalendarGridEvent;
}

/** Grid entries of the loaded events; `title` gives the visible title (private/busy blocks get a localized one). */
/** Not answered yet (needs action): bold and dashed. */
export const needsAnswer = (event: CalendarEvent): boolean => event.isMeeting && !event.isOrganizer && (event.myResponse === 'none' || event.myResponse === 'unknown');

export function toShownEvents(results: readonly { calendarId: string; events: readonly CalendarEvent[] }[], calendars: readonly CalendarInfo[], title: (event: CalendarEvent) => string, categories: readonly Category[] = [], toWall: (instant: string) => Date = (i) => new Date(i)): ShownEvent[] {
  return results.flatMap(({ calendarId, events }) => {
    const calendar = calendars.find((c) => c.id === calendarId);
    if (!calendar) return [];
    return events.map((event) => ({
      event,
      calendar,
      grid: {
        id: `${calendarId}|${event.id}`,
        title: title(event),
        start: toWall(event.start),
        end: toWall(event.end),
        allDay: event.allDay,
        // Event colour, else its first category's colour, else the calendar's (Outlook/Google).
        color: event.color ?? event.categories.map((name) => categoryOf(categories, name)?.color).find(Boolean) ?? calendar.color,
        detail: event.location || undefined,
        tentative: needsAnswer(event),
        pending: needsAnswer(event),
        declined: event.myResponse === 'declined',
        showAs: event.myResponse === 'tentative' ? 'tentative' : event.showAs,
        categories: event.categories.flatMap((name) => categoryOf(categories, name) ?? []),
        muted: event.masked,
        cancelled: event.isCancelled,
      },
    }));
  });
}
